<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { UiButton, UiDialog, UiIcon, UiInputField, UiText } from "@shopexperts/shared";
import type { CustomerStoryMetric } from "@shopexperts/shared";

const props = defineProps<{
    modelValue: CustomerStoryMetric[];
}>();

const open = defineModel<boolean>("open", { default: false });
const emit = defineEmits<{
    (e: "update:modelValue", value: CustomerStoryMetric[]): void;
}>();

interface EditableMetricRow {
    id: number;
    label: string;
    prefix: string;
    suffix: string;
    before_value: string;
    after_value: string;
}

const rows = ref<EditableMetricRow[]>([]);
let nextRowId = 0;

function createEmptyRow(): EditableMetricRow {
    return {
        id: nextRowId++,
        label: "",
        prefix: "",
        suffix: "",
        before_value: "",
        after_value: "",
    };
}

function isRowEmpty(row: EditableMetricRow) {
    return !row.label.trim() && !row.prefix.trim() && !row.suffix.trim();
}

function normalizeRows(nextRows: EditableMetricRow[]) {
    const rowsWithoutTrailingEmpty = [...nextRows];

    while (rowsWithoutTrailingEmpty.length > 0 && isRowEmpty(rowsWithoutTrailingEmpty[rowsWithoutTrailingEmpty.length - 1]!)) {
        rowsWithoutTrailingEmpty.pop();
    }

    rows.value = [...rowsWithoutTrailingEmpty, createEmptyRow()];
}

watch(() => props.modelValue, (value) => {
    normalizeRows(value.map((metric) => ({
        id: nextRowId++,
        label: metric.label,
        prefix: metric.prefix || "",
        suffix: metric.suffix || "",
        before_value: metric.before_value || "",
        after_value: metric.after_value || "",
    })));
}, { immediate: true, deep: true });

watch(rows, (value) => {
    const lastRow = value[value.length - 1];
    if (!lastRow || !isRowEmpty(lastRow)) {
        normalizeRows(value);
    }
}, { deep: true });

const isSaveDisabled = computed(() =>
    rows.value.some((row) => !isRowEmpty(row) && !row.label.trim()),
);

function removeRow(index: number) {
    normalizeRows(rows.value.filter((_, rowIndex) => rowIndex !== index));
}

function isTrailingEmptyRow(row: EditableMetricRow, index: number) {
    return index === rows.value.length - 1 && isRowEmpty(row);
}

function save() {
    emit("update:modelValue", rows.value.map(({ id: _id, ...row }) => ({
        label: row.label.trim(),
        prefix: row.prefix,
        suffix: row.suffix,
        before_value: row.before_value,
        after_value: row.after_value,
    })).filter((row) => row.label));
    open.value = false;
}
</script>

<template>
    <UiDialog v-model="open" title="Edit Metrics" size="lg" hide-close-button>
        <template #default>
            <div class="flex flex-col gap-4">
                <div class="grid grid-cols-[minmax(0,1fr)_minmax(0,120px)_minmax(0,120px)_48px] gap-3">
                    <UiText as="span" variant="small" class="font-semibold text-se-gray-700" text="Label" />
                    <UiText as="span" variant="small" class="font-semibold text-se-gray-700" text="Prefix" />
                    <UiText as="span" variant="small" class="font-semibold text-se-gray-700" text="Suffix" />
                    <span />
                </div>

                <div class="flex flex-col gap-3">
                    <div
                        v-for="(row, index) in rows"
                        :key="row.id"
                        class="grid grid-cols-[minmax(0,1fr)_minmax(0,120px)_minmax(0,120px)_48px] gap-3"
                    >
                        <UiInputField v-model="row.label" class="min-w-0" placeholder="Conversion Rate" />
                        <UiInputField v-model="row.prefix" class="min-w-0" placeholder="$" />
                        <UiInputField v-model="row.suffix" class="min-w-0" placeholder="%" />
                        <UiButton
                            v-if="!isTrailingEmptyRow(row, index)"
                            variant="outline"
                            size="sm"
                            class="relative z-10 justify-center"
                            @click="removeRow(index)"
                        >
                            <template #before_label>
                                <UiIcon name="delete" class="h-4 w-4" />
                            </template>
                        </UiButton>
                        <span v-else />
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-end gap-3">
                <UiButton variant="outline" label="Cancel" @click="open = false" />
                <UiButton variant="primary" label="Save Metrics" :disabled="isSaveDisabled" @click="save" />
            </div>
        </template>
    </UiDialog>
</template>
